import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Spin, Modal, Divider, Row, Col } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import '../../styles/_patientCard.scss';
import { UsDateformat } from '../../utilities/extensions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectPatientResult,
    updatePatientResult,
} from '../../features/patient/patientSlice';
import {
    useFetchTeamOptionsMutation,
    usePatientDetailsMutation,
    usePostPatientAllergyBypassUploadMutation,
    usePostPatientGetHelpMutation,
    usePostPatientScriptSureCookieMutation,
} from '../../features/patient/patientService';
import TextArea from 'antd/lib/input/TextArea';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { setCredentials, setSubMenu } from '../../features/auth/authSlice';
import { bindActionCreators } from 'redux';
import RoundedButton from '../../components/RoundedButton';
import PageTitle from '../../components/PageTitle';
import WarningModal from '../../components/WarningModal';
import { PatientStatusEnum } from '../../models/PatientModels';
import * as psTokens from '../../global/PubSubTokens';
import { subscribe, unsubscribe } from '../../components/Layout/GlobalEvents';


interface PatientData {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    hrxPatientId: string;
    medicalRecordId: string;
    patientStatusDisplay: string;
    patientStatus: PatientStatusEnum;
    dischargedOnEmr: boolean;
}

const PatientCard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [patientDetails] = usePatientDetailsMutation();
    const [teamDetails] = useFetchTeamOptionsMutation();
    const [patientScriptSureCookie] = usePostPatientScriptSureCookieMutation();
    const [patientAllergyBypassUpload] =
        usePostPatientAllergyBypassUploadMutation();
    const [patientGetHelp] = usePostPatientGetHelpMutation();
    const authACs = bindActionCreators({ setSubMenu }, dispatch);
    const patient_details = useSelector(selectPatientResult, shallowEqual);
    const { id: patientId } = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [scriptSureOpen, setScriptSureOpen] = useState<boolean>(false);

    const [allergyModalOpen, setAllergyModalOpen] = useState<boolean>(false);
    const [linkTriggered, setLinkTriggered] = useState<string>();

    const [getHelpModalOpen, setGetHelpModalOpen] = useState<boolean>(false);
    const [getConsentModalOpen, setConsentModalOpen] = useState<boolean>(false);

    const [sourceUrl, setSourceUrl] = useState<string>('');
    const [scriptSureLoading, setScriptSureLoading] = useState<boolean>(true);
    const [helpDescriptionValue, setHelpDescriptionValue] =
        useState<string>('');
    const [monthlyDaysOfCare, setMonthlyDaysOfCare] = useState<any>(0);
    const iframeRef: any = useRef(null);
    const bac = bindActionCreators({ setCredentials }, dispatch);

    let data: PatientData | undefined | any = patient_details?.patientDetails;
    let teamData: any = patient_details?.teamOptions;

    const closeGetHelpModal = () => {
        setLoading(false);
        setGetHelpModalOpen(false);
        setConsentModalOpen(false);
        setHelpDescriptionValue('');
    };

    const submitHelp = async (data: any) => {
        setLoading(true);
        try {
            let loginUserData: any = {};
            const localToken: any = localStorage.getItem('local-token');
            if (localToken !== null) {
                loginUserData = JSON.parse(localToken);
            }

            const sentData = {
                ErrorDescription: {
                    SessionToken:
                        loginUserData?.profile?.scriptSure?.sessiontoken,
                    WidgetDetails: sourceUrl,
                    Notes:
                        helpDescriptionValue === ''
                            ? null
                            : helpDescriptionValue,
                },
                HospiceOrganizationId: data?.hospiceOrganizationId,
                PatientId: data?.patientId,
            };
            if (!window.navigator.onLine) {
                withReactContent(Swal).fire({
                    icon: 'error',
                    title: 'You are Offline',
                    text: 'Please check your internet connection',
                    confirmButtonColor: 'rgb(33,150,243)',
                });
                closeGetHelpModal();
                return;
            }
            const response = await patientGetHelp(sentData);
            if (!response.hasOwnProperty('error')) {
                withReactContent(Swal).fire({
                    text: 'Request submitted successfully',
                    icon: 'success',
                    confirmButtonColor: 'rgb(33,150,243)',
                });
            } else {
                withReactContent(Swal).fire({
                    icon: 'error',
                    text: 'Support request failed',
                    confirmButtonColor: 'rgb(33,150,243)',
                });
            }
            closeGetHelpModal();
        } catch (error) {
            closeGetHelpModal();
            withReactContent(Swal).fire({
                icon: 'error',
                text: 'Support request failed',
            });
            console.log({ error });
        }
    };

    const getScriptSureData = async (SessionToken: any, PatientId: any) => {
        try {
            const response = await patientScriptSureCookie({
                SessionToken,
                PatientId,
            });
            return response;
        } catch (error) {
            console.log({ error });
        }
    };

    const redirectScriptSureUrl = async (data: any, gotoAllergyUrl = false) => {
        try {
            setLoading(true);
            let loginUserData: any = {};
            const localToken: any = localStorage.getItem('local-token');
            if (localToken !== null) {
                loginUserData = JSON.parse(localToken);
            }
            const scriptSurePatientId: string = data.scriptSurePatientId;
            if (!scriptSurePatientId) {
                alert(
                    'ePrescribe Authentication for Patient failed. Patient updates may still be processing. Try reloading the patient again if ePrescribe is failing.',
                );
                setLoading(false);
                return null;
            }
            const loggedUserPatientId: string = data.patientId;
            const loggedUserScriptSureseSsionToken: string =
                loginUserData.profile.scriptSure.sessiontoken;

            const response: any = await getScriptSureData(
                loggedUserScriptSureseSsionToken,
                loggedUserPatientId,
            );

            const scriptSureSessionToken: string = response.data.sessiontoken;
            const webAppUrl: string = response.data.webAppUrl;

            // Updates session token if expired.
            if (
                loginUserData.profile.scriptSure.sessiontoken !==
                response.data.sessiontoken
            ) {
                loginUserData.profile.scriptSure = response.data;
                bac.setCredentials(loginUserData);
            }

            if (!gotoAllergyUrl) {
                const sourceUrl: string = `${webAppUrl}/chart/${scriptSurePatientId}/prescriptions?sessiontoken=${scriptSureSessionToken}&darkmode=off`;
                setSourceUrl(sourceUrl);
            } else {
                const sourceUrl: string = `${webAppUrl}/widgets/allergy/${scriptSurePatientId}?sessiontoken=${scriptSureSessionToken}&darkmode=off`;
                setSourceUrl(sourceUrl);
            }
            setScriptSureOpen(true);
            setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    const gotoAllergy = () => {
        setAllergyModalOpen(false);
        redirectScriptSureUrl(data, true);
    };

    const onCloseAllergyModal = () => {
        setAllergyModalOpen(false);
        setLinkTriggered(undefined);
    };

    const confirmAllergyModal = async (data: any) => {
        setAllergyModalOpen(false);
        try {
            let loginUserData: any = {};
            const localToken: any = localStorage.getItem('local-token');
            if (localToken !== null) {
                loginUserData = JSON.parse(localToken);
            }
            const sentData = {
                ClinicianId: loginUserData.profile.id,
                PatientId: data.patientId,
                BypassedOn: new Date(),
            };
            const response = await patientAllergyBypassUpload(sentData);
            if (response) {
                if (linkTriggered) {
                    navigate(linkTriggered);
                } else {
                    redirectScriptSureUrl(data);
                }
            }
        } catch (error) {
            console.log({ error });
        }
    };

    const onClickEprescribe = async (data: any) => {
        if (data?.allergiesConfirmed && data?.consent) {
            await redirectScriptSureUrl(data);
        } else if (!data?.consent) {
            setConsentModalOpen(true);
        } else if (!data?.allergiesConfirmed) {
            setAllergyModalOpen(true);
        }
    };

    const onClickScriptSureRelated = (data: any, link: string) => {
        setLinkTriggered(link);

        if (!data?.allergiesConfirmed) {
            setAllergyModalOpen(true);
        } else if (!data?.consent) {
            setConsentModalOpen(true);
        }
    };

    const getLinkScriptSureRelated = (data: any, link: string, noLink:string | undefined = undefined) => {
        if (!data?.allergiesConfirmed || !data?.consent) {
            return noLink;
        }

        return link;
    };

    const getPatientDetails = async (_id: any) => {
        try {
            const response = await patientDetails(_id);
            return response;
        } catch (error) {
            return error;
        }
    };
    const getTeamDetails = async () => {
        try {
            const response = await teamDetails('');
            return response;
        } catch (error) {
            return error;
        }
    };

    const getMonthlyDaysOfCare = (data: any) => {
        if (data) {
            if (
                (data?.patientStatusDisplay === 'Admitted' &&
                    typeof data?.eligibilityStartDate === 'string') ||
                typeof data?.eligibilityEndDate === 'string'
            ) {
                let date: any = new Date();
                const [currentYear, currentMonth, currentDate]: Array<any> = [
                    date.getFullYear(),
                    date.getMonth() + 1,
                    date.getDate(),
                ];

                if (data?.patientStatusDisplay === 'Admitted') {
                    const [
                        eligibilityStartYear,
                        eligibilityStartMonth,
                        eligibilityStartDay,
                    ]: Array<any> = (data?.eligibilityStartDate)
                        .slice(0, 10)
                        .split('-');

                    if (
                        currentYear - eligibilityStartYear === 0 &&
                        currentMonth - eligibilityStartMonth === 0
                    ) {
                        setMonthlyDaysOfCare(
                            currentDate - eligibilityStartDay + 1,
                        );
                    } else {
                        setMonthlyDaysOfCare(currentDate);
                    }
                } else {
                    const [
                        eligibilityEndYear,
                        eligibilityEndMonth,
                        eligibilityEndDay,
                    ] = (data?.eligibilityEndDate).slice(0, 10).split('-');
                    if (
                        currentYear - eligibilityEndYear === 0 &&
                        currentMonth - eligibilityEndMonth === 0
                    ) {
                        setMonthlyDaysOfCare(
                            currentDate - eligibilityEndDay + 1,
                        );
                    }
                }
            }
        }
    };

    const getPatientData = async (patientId: any) => {
        const response: any = await getPatientDetails(patientId);
        data = response;
        dispatch(updatePatientResult(patientId));
        setLoading(false);
        getMonthlyDaysOfCare(response?.data);
    };

    const getTeamData = async () => {
        const response = await getTeamDetails();
        data = response;
        // dispatch(updatePatientResult(patientId));
        setLoading(false);
    };

    const renderModalContent = () => {
        return (
            <>
                <WarningModal
                    title="Drug History"
                    body="You have not completed allergies information, would you like to bypass this resource?"
                />
            </>
        );
    };

    const renderConsentModalContent = () => {
        return (
            <>
                <WarningModal
                    title="Drug List"
                    body="Patient has not consented to download of their medical information."
                />
            </>
        );
    };

    const renderGetHelpModalContent = () => {
        return (
            <Spin spinning={loading} size="large">
                <div className="display-flex">
                    <p className="para-styles">Get Help</p>
                </div>
                <div className="error-btn m-1 mb-3">
                    Please provide a brief description of the issue you are
                    facing with the e-prescribe functionality
                </div>
                <div>
                    <TextArea
                        rows={4}
                        placeholder="Description"
                        value={helpDescriptionValue}
                        onChange={(e) =>
                            setHelpDescriptionValue(e.target.value)
                        }
                    />
                </div>
            </Spin>
        );
    };

    const backNavigateFromScriptSureIframe = async () => {
        setLoading(true);
        await getPatientData(patientId);
        setScriptSureOpen(false);
    };

    useEffect(() => {
        getPatientData(patientId);
        setLoading(true);
        getMonthlyDaysOfCare(data);
        if (
            (Boolean(teamData) && JSON.stringify(teamData) === '{}') ||
            teamData.length === 0
        ) {
            getTeamData();
            setLoading(true);
        }
        subscribe(psTokens.SubMenuEvents, subMenuEvents);
        return () => {
            unsubscribe(psTokens.SubMenuEvents, subMenuEvents);
        };
    }, []);

    const subMenuEvents = ({ detail }: { detail: any }) => {
        const { type, payload } = detail;
        switch (type) {
            case psTokens.EventTypeConsentModalOpen:
                setConsentModalOpen(payload);
                break;
            case psTokens.EventTypeClickEPrescribe:
                onClickEprescribe(payload);
                break;
            case psTokens.EventTypeScriptSureRelatedAddNewMedication:
                onClickScriptSureRelated(payload, `add-new-medication`);
                break;
            case psTokens.EventTypeScriptSureRelatedApproveMedicationsView:
                onClickScriptSureRelated(payload, `approve-medications/view`);
                break;
            case psTokens.EventTypeScriptSureRelatedMedicationsHistory:
                onClickScriptSureRelated(payload, `medications-history`);
                break;
        }
    };

    useEffect(() => {
        const subMenu:any = [
            {
                link: data?.consent
                    ? `${location.pathname}/diagnosis`
                    : `${location.pathname}`,
                title: 'Patient Diagnosis',
                iconSource:
                    '/assets/rx-images/patient-dashboard/patient-diagnosis.png',
                eventType: psTokens.EventTypeConsentModalOpen,
                eventPayload: true,
                isDivider: false
            },
            {
                link: data?.consent
                    ? `${location.pathname}/allergies`
                    : `${location.pathname}`,
                title: 'Patient Allergies',
                showStatus: true,
                status: data?.allergiesConfirmed,
                iconSource:
                    '/assets/rx-images/patient-dashboard/patient-allergies.png',
                eventType: psTokens.EventTypeConsentModalOpen,
                eventPayload: true,
                isDivider: false
            },
            {
                mainText: 'Medications',
                withTooltip: true,
                toolTipText: "Access '",
                toolTipTextUnderline: "Legacy ePrescribe'",
                eventType: psTokens.EventTypeClickEPrescribe,
                eventPayload: data,
                isDivider: true,
            },
            {
                title: 'Add New Medication',
                iconSource:
                    '/assets/rx-images/patient-dashboard/add-new-med.png',
                link: getLinkScriptSureRelated(data, `${location.pathname}/add-new-medication`, '#'),
                eventType: psTokens.EventTypeScriptSureRelatedAddNewMedication,
                eventPayload: data,
                isDivider: false
            },
            {
                link: getLinkScriptSureRelated(data, `${location.pathname}/approve-medications/view`, '#'),
                title: 'Review Medication',
                iconSource:
                    '/assets/rx-images/patient-dashboard/approve-med.png',
                eventType: psTokens.EventTypeScriptSureRelatedApproveMedicationsView,
                eventPayload: data,
                isDivider: false
            },
            {
                link: getLinkScriptSureRelated(data, `${location.pathname}/medications-history`, '#'),
                title: 'Med Sheets & Re-Prescribe',
                iconSource:
                    '/assets/rx-images/patient-dashboard/med-history.png',
                eventType: psTokens.EventTypeScriptSureRelatedMedicationsHistory,
                eventPayload: data,
                isDivider: false
            },
            {
                mainText: ' Manage Patient',
                withTooltip: false,
                isDivider: true,
            }
        ];
        if (data?.patientStatus === PatientStatusEnum.Admitted) {
            subMenu.push({
                title: 'Discharge Patient',
                iconSource: '/assets/rx-images/patient-dashboard/discharge.png',
                link: `/patient-discharge/${patientId}`,
                isDivider: false,
            });
        }
        if (
            data?.patientStatus === PatientStatusEnum.Invalid ||
            data?.patientStatus === PatientStatusEnum.DischargedDeceased ||
            data?.patientStatus === PatientStatusEnum.DischargedRevoked ||
            data?.patientStatus === PatientStatusEnum.DischargedTransferred
        ) {
            subMenu.push({
                title: 'Re-Admit',
                iconSource: '/assets/rx-images/patient-dashboard/discharge.png',
                link: `${location.pathname}/patient/${patientId}/re-admit`,
                isDivider: false,
            });
        }
        authACs.setSubMenu(subMenu);
    }, [data, patientId, location]);

    const getPatientTeamName = () => {
        if (teamData?.data && teamData?.data.length > 0) {
            return teamData?.data.find(
                (team: any) => team.code === data.teamCode,
            );
        }
        return { name: 'N/A' };
    };

    const getPatientGender = useMemo(() => {
        if (data?.demographics?.gender) {
            return data?.demographics?.gender?.toLowerCase().includes('f')
                ? 'Female'
                : data?.demographics?.gender?.toLowerCase().includes('m')
                ? 'Male'
                : 'N/A';
        }

        return 'N/A';
    }, [data?.demographics?.gender]);

    return (
        <Spin spinning={loading} size="large">
            <PageTitle
                title={data?.demographics?.firstName + ' ' + data?.demographics?.lastName }
                onClick={() =>
                    scriptSureOpen
                        ? backNavigateFromScriptSureIframe()
                        : navigate('/dashboard')
                }>
                {scriptSureOpen && (
                    <div className="get-help">
                        <button
                            type="button"
                            style={{ backgroundColor: 'rgb(33,150,243)' }}
                            className="btn btn-primary btn-sm"
                            onClick={() => setGetHelpModalOpen(true)}
                        >
                            Get Help
                        </button>
                    </div>
                )}
            </PageTitle>

            {!scriptSureOpen && (
                <Card
                    className="mt-3 me-3 team-name-container"
                    style={{ marginLeft: 28 }}
                >
                    <span className="team-name-title">Patient’s Team:</span>
                    <span className="team-name ms-1">
                        {getPatientTeamName()?.name}
                    </span>
                </Card>
            )}
            {scriptSureOpen && (
                <div className="scriptsure-iframe" style={{ height: '100vh' }}>
                    <iframe
                        src={sourceUrl}
                        id="scriptsure-iframe"
                        ref={iframeRef}
                        width="100%"
                        loading={'lazy'}
                        height="100%"
                        title="HospisRx"
                        onLoad={() => setScriptSureLoading(false)}
                    />
                </div>
            )}
            {scriptSureLoading && scriptSureOpen && (
                <Spin spinning={scriptSureLoading} size="large" />
            )}
            {scriptSureOpen ? null : (
                <div className="card-style pt-4">
                    <Card className="shadow-sm mb-5 bg-body w-100">
                        <div className="display-flex align-items-center justify-content-between">
                            <div className="patient-info-title mt-2">
                                Patient Information
                            </div>
                            <RoundedButton
                                className="mt-2 me-2"
                                style={{
                                    backgroundColor: 'var(--hcrx-light-blue)',
                                    color: 'white',
                                }}
                                onClick={() => {
                                    navigate(
                                        `/patient_record/${data.patientId}`,
                                        {
                                            state: {
                                                action: 'EDIT',
                                            },
                                        },
                                    );
                                }}
                            >
                                View/Edit
                            </RoundedButton>
                        </div>
                        <Divider className="mt-3" />
                        <Row className="border border-dark-subtle mx-2">
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                First Name
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.demographics?.firstName}
                            </Col>
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Last Name
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.demographics?.lastName}
                            </Col>
                        </Row>
                        <Row className="border border-top-0 border-dark-subtle mx-2">
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Date of Birth
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {UsDateformat(data?.demographics?.dateOfBirth)}
                            </Col>
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Gender
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {getPatientGender}
                            </Col>
                        </Row>
                        <Row className="border border-top-0 border-dark-subtle mx-2">
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                HCRx Patient ID
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.hrxPatientId}
                            </Col>
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Medical Record
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.medicalRecordId}
                            </Col>
                        </Row>
                        <Row className="border border-top-0 border-dark-subtle mx-2">
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Start of Care Date
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {UsDateformat(data?.eligibilityStartDate)}
                            </Col>
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                }}
                            >
                                Current Month Days of Care
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {monthlyDaysOfCare}
                            </Col>
                        </Row>

                        <div className="patient-info-title my-2 mt-3">
                            Primary Care Practitioner
                        </div>

                        <Row className="border border-dark-subtle mx-2 mt-3">
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                First Name
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.primaryCarePractitioner?.firstName}
                            </Col>
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Last Name
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={6}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.primaryCarePractitioner?.lastName}
                            </Col>
                        </Row>
                        <Row className="border border-top-0 border-dark-subtle mx-2">
                            <Col
                                span={5}
                                className="p-3"
                                style={{
                                    backgroundColor: '#F0F0F0',
                                    color: '#58595B',
                                    fontSize: 14,
                                    paddingLeft: 20,
                                }}
                            >
                                Credential
                            </Col>
                            <Col
                                className="bg-white p-3"
                                span={12}
                                style={{ color: '#58595B', fontSize: 14 }}
                            >
                                {data?.primaryCarePractitioner?.userId}
                            </Col>
                        </Row>
                    </Card>
                </div>
            )}
            {getConsentModalOpen && (
                <Modal
                    open={getConsentModalOpen}
                    closable={false}
                    onCancel={() => setConsentModalOpen(false)}
                    footer={[
                        <Button
                            key="gotoallergy"
                            type="primary"
                            className="cancel-btn"
                            onClick={() => setConsentModalOpen(false)}
                        >
                            {'OK'}
                        </Button>,
                    ]}
                >
                    {renderConsentModalContent()}
                </Modal>
            )}
            {allergyModalOpen && (
                <Modal
                    open={allergyModalOpen}
                    closable={false}
                    onCancel={onCloseAllergyModal}
                    footer={[
                        <Button
                            key="gotoallergy"
                            type="primary"
                            className="go-allergy-btn"
                            onClick={() => gotoAllergy()}
                        >
                            {'Go to allergies'}
                        </Button>,
                        <Button
                            key="confirm"
                            type="primary"
                            className="confirm-allergy-btn"
                            onClick={() => confirmAllergyModal(data)}
                        >
                            {'Confirm'}
                        </Button>,
                    ]}
                >
                    {renderModalContent()}
                </Modal>
            )}
            {getHelpModalOpen && (
                <Modal
                    open={getHelpModalOpen}
                    closable={false}
                    footer={[
                        <Button
                            key="gotoallergy"
                            disabled={loading}
                            type="primary"
                            className="cancel-btn"
                            onClick={() => closeGetHelpModal()}
                        >
                            {'Cancel'}
                        </Button>,
                        <Button
                            key="Submit"
                            disabled={loading}
                            type="primary"
                            className="submit-help"
                            onClick={() => submitHelp(data)}
                        >
                            {'Submit'}
                        </Button>,
                    ]}
                >
                    {renderGetHelpModalContent()}
                </Modal>
            )}
        </Spin>
    );
};

export default PatientCard;
